<template>
  <b-card
    :title="header"
    body-class="mx-3 mx-md-5 mx-lg-5 mx-xl-5 px-3 px-md-5 px-lg-5 px-xl-5"
    class="mt-5 p-0 p-md-5 p-lg-5 p-xl-5 w-75"
    style="/**width: 550px**/"
  >
    <b-form onkeydown="return event.key != 'Enter'">
      <p class="pb-2 grey--text">{{ subheader }}</p>

      <b-form-group
        id="input-group-password"
        label="Password"
        label-for="input-password"
        v-if="currentStep === 1"
      >
        <b-form-input
          id="input-password"
          v-model="form.password"
          type="password"
          required
          placeholder="Enter password"
          :state="state.password"
          aria-describedby="input-password-feedback"
          @keyup.enter="nextStep"
        ></b-form-input>

        <b-form-invalid-feedback id="input-password-feedback">
          <!-- eslint-disable-next-line -->
          {{ errors.password }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        id="input-group-email"
        label="Email Address"
        label-for="input-email"
        v-else-if="currentStep === 2"
      >
        <b-form-input
          id="input-email"
          v-model="form.email"
          type="text"
          required
          placeholder="Enter email"
          :state="state.email"
          aria-describedby="input-email-feedback"
          @keyup.enter="nextStep"
        ></b-form-input>

        <b-form-invalid-feedback id="input-email-feedback">
          <!-- eslint-disable-next-line -->
          {{ errors.email }}
        </b-form-invalid-feedback>
        <div class="email-suggestion" v-if="!errors.email && emailSuggestion">
          Did you mean
          <a href="#" @click.prevent="applyEmailSuggestion">{{
            emailSuggestion
          }}</a
          >?
        </div>
      </b-form-group>

      <b-form-group id="input-group-code" v-else>
        <code-input
          :key="codeInputKey"
          :loading="false"
          @complete="onCompleteCode"
        />

        <div class="invalid-feedback d-block mt-2">{{ errors.code }}</div>

        <div class="mt-2">
          <b-button
            variant="link"
            class="p-0 resend-code-btn"
            :disabled="resending || loading || cooldown > 0"
            @click="onResendCode"
          >
            Resend code
            <span v-if="cooldown > 0">({{ cooldown }}s)</span>
          </b-button>
          <span v-if="resendNotice" class="text-success ml-2 small">{{
            resendNotice
          }}</span>
        </div>
      </b-form-group>

      <!-- <hr class="modal-divider mt-5" /> -->

      <!-- <div class="d-flex justify-content-between">
        <div class="w-100 mt-2 mb-1">
          <b-button
            variant="outline-secondary"
            class="w-25 mr-2 btn-outlined bg-white text-dark"
            :to="{ name: 'user.settings-page', hash: '#account' }"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            class="w-25 ml-2 float-right"
            :disabled="loading"
            @click="nextStep"
          >
            <b-spinner v-if="loading" small label="Loading" />&nbsp;Continue
          </b-button>
        </div>
      </div> -->
      <div class="justify-content-between">
        <b-container fluid>
          <b-row cols="2" class="verify-actions">
            <b-col cols="6" class="text-center text-lg-right text-xl-right">
              <b-button
                class="w-75 mr-0 mr-lg-2 mr-xl-2 btn-outlined bg-white text-dark"
                variant="primary"
                :to="{ name: 'user.settings-page', hash: '#account' }"
              >
                Cancel
              </b-button>
            </b-col>
            <b-col cols="6" class="text-center text-lg-left text-xl-left">
              <b-button
                variant="primary"
                class="w-75 ml-0 ml-lg-2 ml-xl-2"
                :disabled="loading"
                @click="nextStep"
              >
                <b-spinner v-if="loading" small label="Loading" />&nbsp;Continue
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CodeInput from '@/components/inputs/CodeInput'
import { normalizeEmail, suggestEmailCorrection } from '@/utils/emailPolicy'

export default {
  components: {
    CodeInput,
  },

  computed: {
    ...mapState({
      verificationToken: (state) => state.auth.verificationToken,
      user: (state) => state.auth.user,
    }),
  },

  data() {
    return {
      loading: false,
      resending: false,
      cooldown: 0,
      cooldownTimer: null,
      resendNotice: null,
      codeInputKey: 0,
      header: 'Verify Password',
      subheader: 'Enter your current password to change your email',
      currentStep: 1,
      form: {
        password: null,
        email: null,
        code: null,
      },
      state: {
        password: null,
        email: null,
      },
      errors: {
        password: null,
        email: null,
        code: null,
      },
      emailSuggestion: null,
    }
  },

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    }
  },

  methods: {
    ...mapActions({
      getVerificationToken: 'auth/getVerificationToken',
      requestChangeEmail: 'auth/requestChangeEmail',
      verifyChangeEmail: 'auth/verifyChangeEmail',
    }),

    startCooldown(seconds = 30) {
      this.cooldown = seconds
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        if (this.cooldown <= 1) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.cooldown = 0
        } else {
          this.cooldown -= 1
        }
      }, 1000)
    },

    async onResendCode() {
      if (this.resending || this.loading || this.cooldown > 0) return

      this.resending = true
      this.resendNotice = null
      this.errors.code = null

      try {
        await this.requestChangeEmail({ email: this.form.email })
        this.resendNotice = 'A new code has been sent.'
        this.codeInputKey += 1
        this.startCooldown()
      } catch (error) {
        const d = error.response && error.response.data
        const msg =
          (d && d.message) || 'Unable to resend code. Please try again shortly.'
        this.$bvToast.toast(msg, {
          title: 'Resend failed',
          variant: 'danger',
          autoHideDelay: 5000,
          solid: true,
        })
      } finally {
        this.resending = false
      }
    },

    nextStep() {
      if (this.currentStep === 1) {
        this.generateToken()
      }
      if (this.currentStep === 2) {
        this.changeEmailRequest()
      }
    },

    onCompleteCode(v) {
      this.form.code = v
      this.changeEmailVerify()
    },

    applyEmailSuggestion() {
      if (!this.emailSuggestion) return
      this.form.email = this.emailSuggestion
      this.emailSuggestion = null
    },

    async generateToken() {
      this.loading = true
      await this.getVerificationToken({
        password: this.form.password,
      })
        .then(() => {
          this.currentStep++
          this.header = 'New Email Address'
          this.subheader = 'Enter new email address'
        })
        .catch((error) => {
          if (error.response.data) {
            this.state.password = false
            this.errors.password = error.response.data.message
          }
        })
      this.loading = false
    },

    async changeEmailRequest() {
      this.loading = true
      this.form.email = normalizeEmail(this.form.email)
      await this.requestChangeEmail({ email: this.form.email })
        .then(() => {
          this.currentStep++
          this.header = 'Verify Email'
          this.subheader = `Input the code sent to: ${this.form.email}`
          this.startCooldown()
        })
        .catch((error) => {
          if (error.response.data) {
            this.state.email = false
            this.errors.email = error.response.data.message
          }
        })
      this.loading = false
    },

    async changeEmailVerify() {
      this.loading = true
      await this.verifyChangeEmail({
        token: this.form.code,
        email: this.form.email,
      })
        .then(() => {
          this.$router.push({
            name: 'user.settings-page',
            hash: '#account',
          })
        })
        .catch((error) => {
          const d = error.response && error.response.data
          const msg = d
            ? d.message ||
              (d.errors && Object.values(d.errors).flat()[0]) ||
              'Invalid or expired verification code.'
            : error.message || 'Invalid or expired verification code.'
          if (d) {
            this.form.code = null
            this.errors.code = d.message || msg
          }
          this.$bvToast.toast(msg, {
            title: 'Verification failed',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true,
          })
        })
      this.loading = false
    },
  },

  watch: {
    'form.password'() {
      this.state.password = this.form.password.length !== 0 ? null : false
      this.errors.password = 'This field is required'
    },

    'form.email'() {
      this.state.email = this.form.email.length !== 0 ? null : false
      this.errors.email = 'This field is required'
      this.emailSuggestion = suggestEmailCorrection(this.form.email)
    },
  },

  created() {
    this.header = this.user.has_password
      ? 'Verify Password'
      : 'New Email Address'
    this.subheader = this.user.has_password
      ? 'Enter your current password to change your email'
      : 'Enter new email address'
    this.currentStep = this.user.has_password ? 1 : 2
  },
}
</script>

<style lang="scss" scoped>
.btn.btn-outlined {
  padding: 4px 32px;
}
.btn {
  height: 36px;
}

/* Large screens: the buttons sit as a natural-size pair on the left, aligned
   with the rest of the content. Each column keeps the old button footprint
   (75% of a half-width column = 37.5% of the row). */
@media (min-width: 992px) {
  .verify-actions {
    justify-content: flex-start;
  }

  .verify-actions > [class*='col'] {
    flex: 0 0 auto;
    width: 37.5%;
    max-width: none;
  }

  .verify-actions .btn {
    width: 100% !important;
  }
}

/* Small screens (< lg): centre the heading + description, let the card use the
   full width, trim its heavy body padding, and show the buttons side-by-side
   filling their half-columns with a clear gap, down to 320px. */
@media (max-width: 991.98px) {
  .card.w-75 {
    width: 100% !important;
    margin-top: 2rem !important;
  }

  .card /deep/ .card-body {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 1.25rem !important;
  }

  .card /deep/ .card-title,
  .card .grey--text {
    text-align: center;
  }

  .verify-actions .btn {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .verify-actions > [class*='col']:first-child {
    padding-right: 0.5rem !important;
  }

  .verify-actions > [class*='col']:last-child {
    padding-left: 0.5rem !important;
  }
}
</style>
